import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Skeleton } from '@mui/material';
import { isValidMapCenter, useMapCenter } from '../../utils/mapCenter';

export interface ZoneMapItem {
  id: string;
  code: string;
  name: string;
  centerLat: number;
  centerLng: number;
  radiusKm?: number;
  color?: string;
}

interface ZoneMapPanelProps {
  zones?: ZoneMapItem[];
  selectedId?: string | null;
  onSelectZone?: (id: string) => void;
  onMapClick?: (lat: number, lng: number) => void;
  center?: { lat: number; lng: number };
  pin?: { lat: number; lng: number } | null;
  zoom?: number;
  height?: number | string | { xs?: number | string; sm?: number | string; md?: number | string };
}

type LeafletMap = {
  setView: (c: [number, number], z: number) => LeafletMap;
  fitBounds: (b: unknown, o?: { padding?: [number, number] }) => void;
  on: (e: string, fn: (ev: { latlng: { lat: number; lng: number } }) => void) => LeafletMap;
  off: (e: string, fn?: (ev: { latlng: { lat: number; lng: number } }) => void) => LeafletMap;
  remove: () => void;
  invalidateSize: () => void;
  getZoom?: () => number;
};

type LeafletLayer = { addTo: (m: LeafletMap) => LeafletLayer; on: (e: string, fn: () => void) => LeafletLayer; remove: () => void; setLatLng?: (p: [number, number]) => void };

type LeafletApi = {
  map: (el: HTMLElement, o?: Record<string, unknown>) => LeafletMap;
  tileLayer: (url: string, o?: Record<string, unknown>) => LeafletLayer;
  circle: (latlng: [number, number], o?: Record<string, unknown>) => LeafletLayer;
  marker: (latlng: [number, number], o?: Record<string, unknown>) => LeafletLayer;
  latLngBounds: (latlngs: [number, number][]) => { extend: (p: [number, number]) => void; isValid: () => boolean };
};

declare global {
  interface Window {
    L?: LeafletApi;
  }
}

let leafletPromise: Promise<void> | null = null;

function loadLeaflet(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.L) return Promise.resolve();

  if (!leafletPromise) {
    leafletPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-leaflet-css]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        css.setAttribute('data-leaflet-css', '1');
        document.head.appendChild(css);
      }

      if (document.querySelector('script[data-leaflet-js]')) {
        const wait = setInterval(() => {
          if (window.L) { clearInterval(wait); resolve(); }
        }, 50);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.async = true;
      script.setAttribute('data-leaflet-js', '1');
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load map'));
      document.head.appendChild(script);
    });
  }
  return leafletPromise;
}

export default function ZoneMapPanel({
  zones = [],
  selectedId,
  onSelectZone,
  onMapClick,
  center,
  pin,
  zoom = 11,
  height = 420,
}: ZoneMapPanelProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<LeafletMap | null>(null);
  const layersRef = useRef<LeafletLayer[]>([]);
  const pinMarkerRef = useRef<LeafletLayer | null>(null);
  const clickHandlerRef = useRef<((e: { latlng: { lat: number; lng: number } }) => void) | null>(null);
  const onMapClickRef = useRef(onMapClick);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preferred = isValidMapCenter(center)
    ? center
    : (zones[0] && Number.isFinite(zones[0].centerLat) && Number.isFinite(zones[0].centerLng)
      ? { lat: zones[0].centerLat, lng: zones[0].centerLng }
      : undefined);
  const { center: resolvedCenter, ready: centerReady } = useMapCenter(preferred);

  onMapClickRef.current = onMapClick;

  useEffect(() => {
    loadLeaflet()
      .then(() => setReady(true))
      .catch((e: Error) => setError(e.message));
  }, []);

  // Init map once current location (or cache) is known — never a world/Bangalore flash.
  useEffect(() => {
    if (!ready || !centerReady || !resolvedCenter || !mapRef.current || !window.L || mapInstance.current) return;
    const L = window.L;
    mapInstance.current = L.map(mapRef.current, { zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(mapInstance.current);
    mapInstance.current.setView([resolvedCenter.lat, resolvedCenter.lng], zoom);
    setTimeout(() => mapInstance.current?.invalidateSize(), 100);
  }, [ready, centerReady, resolvedCenter?.lat, resolvedCenter?.lng, zoom]);

  // Bind click handler (stable — uses ref)
  useEffect(() => {
    if (!ready || !mapInstance.current) return;

    if (clickHandlerRef.current) {
      mapInstance.current.off('click', clickHandlerRef.current);
      clickHandlerRef.current = null;
    }

    if (onMapClick) {
      const handler = (e: { latlng: { lat: number; lng: number } }) => {
        onMapClickRef.current?.(e.latlng.lat, e.latlng.lng);
      };
      clickHandlerRef.current = handler;
      mapInstance.current.on('click', handler);
    }
  }, [ready, !!onMapClick]);

  // Zones + view
  useEffect(() => {
    if (!ready || !mapInstance.current || !window.L) return;
    const L = window.L;

    layersRef.current.forEach((layer) => layer.remove());
    layersRef.current = [];

    const boundsPoints: [number, number][] = [];

    zones.forEach((zone) => {
      const lat = Number(zone.centerLat);
      const lng = Number(zone.centerLng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      const isSelected = zone.id === selectedId;
      const color = zone.color || '#2563eb';
      const pos: [number, number] = [lat, lng];
      boundsPoints.push(pos);

      const circle = L.circle(pos, {
        radius: (zone.radiusKm || 5) * 1000,
        color,
        fillColor: color,
        fillOpacity: isSelected ? 0.28 : 0.14,
        weight: isSelected ? 3 : 1.5,
      }).addTo(mapInstance.current!);

      if (onSelectZone) {
        circle.on('click', () => onSelectZone(zone.id));
      }

      const marker = L.marker(pos, { title: zone.name }).addTo(mapInstance.current!);
      if (onSelectZone) {
        marker.on('click', () => onSelectZone(zone.id));
      }

      layersRef.current.push(circle, marker);
    });

    if (boundsPoints.length > 0) {
      const bounds = L.latLngBounds(boundsPoints);
      if (bounds.isValid()) {
        mapInstance.current.fitBounds(bounds, { padding: [40, 40] });
      }
    } else if (resolvedCenter) {
      mapInstance.current.setView([resolvedCenter.lat, resolvedCenter.lng], zoom);
    }
  }, [ready, zones, selectedId, onSelectZone, center?.lat, center?.lng, resolvedCenter?.lat, resolvedCenter?.lng, zoom]);

  // Pin marker for hub coordinate picker
  useEffect(() => {
    if (!ready || !mapInstance.current || !window.L) return;
    const L = window.L;

    if (pin && Number.isFinite(pin.lat) && Number.isFinite(pin.lng)) {
      const pos: [number, number] = [pin.lat, pin.lng];
      if (pinMarkerRef.current) {
        pinMarkerRef.current.setLatLng?.(pos);
        mapInstance.current.setView(pos, mapInstance.current.getZoom?.() ?? zoom);
      } else {
        const icon = (L as LeafletApi & {
          divIcon?: (o: Record<string, unknown>) => unknown;
        }).divIcon?.({
          className: 'restaurant-map-pin',
          html: `<div style="
            width:28px;height:28px;margin-left:-14px;margin-top:-28px;
            background:#e11d48;border:3px solid #fff;border-radius:50% 50% 50% 0;
            transform:rotate(-45deg);
            box-shadow:0 2px 8px rgba(15,23,42,0.35);
          "></div>`,
          iconSize: [28, 28],
          iconAnchor: [14, 28],
        });
        pinMarkerRef.current = L.marker(pos, {
          title: 'Restaurant location',
          ...(icon ? { icon } : {}),
        }).addTo(mapInstance.current);
        mapInstance.current.setView(pos, Math.max(zoom, 14));
      }
    } else if (pinMarkerRef.current) {
      pinMarkerRef.current.remove();
      pinMarkerRef.current = null;
    }
  }, [ready, pin?.lat, pin?.lng]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!ready || !centerReady) {
    const skeletonHeight = typeof height === 'object' ? 280 : height;
    return <Skeleton variant="rounded" height={skeletonHeight} sx={{ borderRadius: 3 }} />;
  }

  return (
    <Box
      ref={mapRef}
      sx={{
        width: '100%',
        height,
        minHeight: 220,
        borderRadius: 3,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        cursor: onMapClick ? 'crosshair' : 'default',
        '& .leaflet-container': { height: '100%', width: '100%', zIndex: 0 },
      }}
    />
  );
}
